/**
 * /open-site?site=<url>: where the free audit's Sign in link lands once the
 * visitor is signed in (FreeToolSessionChrome.tsx signInUrl). It opens that
 * site in team review inside their own workspace (lib/openSite.ts): their
 * project for the site, or a new one, then a team review with Auto Review's
 * panel open. Same frame as ReviewHandoffPage: a spinner naming what is being
 * opened, and on failure the reason with Try again and Back to dashboard.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { toUserFacingMessage } from '@overlayqa/shared';
import { AppLoadingShell } from '../components/AppLoadingShell';
import { useAppSession } from '../hooks/useAppSession';
import { useCreateProject, useProject, useProjects } from '../hooks/useProjects';
import { useCreateLiveReview } from '../hooks/useClientReviews';
import { useExtensionDetection } from '../hooks/useExtensionDetection';
import { extensionRecoveryHandoff, findSiteProject, openSite, OpenSiteError } from '../lib/openSite';
import { readPendingExtensionOpen, writePendingExtensionOpen } from '../lib/pendingExtensionOpen';
import { captureFreeToolSite, clearFreeToolSite, freeToolSiteFromParam, readFreeToolSite } from '../lib/freeToolSite';
import { capture } from '../lib/analytics';
import { log } from '../lib/logger';

const FALLBACK = 'The review could not be opened. Try again in a moment.';

export function OpenSitePage() {
  const [params] = useSearchParams();
  const extensionMode = params.get('mode') === 'extension';
  const requestedProjectId = extensionMode ? params.get('project') ?? '' : '';
  // Stashed too (an idempotent write), so onboarding can start from it when this account is new.
  const [site] = useState(() => extensionMode ? freeToolSiteFromParam(params.get('site'))
    : captureFreeToolSite(`?${params.toString()}`) ?? freeToolSiteFromParam(params.get('site')) ?? readFreeToolSite());
  const { data: session, error: sessionError, isFetching: sessionFetching, refetch: refetchSession } = useAppSession();
  const teamId = session?.activeTeamId ?? null;
  // ProtectedRoute does not hold the onboarding guard (Layout does), so a new account is sent there first.
  const needsOnboarding = !extensionMode && session?.user?.onboardingCompleted === false;
  const projects = useProjects(extensionMode ? undefined : teamId ?? undefined, { includePreviews: false, includeCounts: false });
  const requestedProject = useProject(session?.user?.id ? requestedProjectId : '');
  const createProject = useCreateProject();
  const createLiveReview = useCreateLiveReview();
  const { detected: extensionDetected } = useExtensionDetection();
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // One run per attempt (StrictMode re-runs effects), and the project an earlier attempt created.
  const ranAttempt = useRef(-1);
  const createdProjectId = useRef<string | null>(null);
  const host = site ? new URL(site).hostname : '';

  useEffect(() => {
    if (extensionMode && !session && sessionError && !sessionFetching) {
      setError(toUserFacingMessage(sessionError, 'Your account could not be loaded. Try again.'));
    }
  }, [extensionMode, session, sessionError, sessionFetching]);

  useEffect(() => {
    if (!site || needsOnboarding || !teamId || ranAttempt.current === attempt) return;
    if (extensionMode) {
      if (!session || (requestedProjectId && (requestedProject.isLoading || requestedProject.isFetching))) return;
      ranAttempt.current = attempt;
      try {
        if (requestedProject.isError) throw requestedProject.error;
        const handoff = extensionRecoveryHandoff(site, requestedProjectId, requestedProject.data, session);
        writePendingExtensionOpen(handoff);
        if (readPendingExtensionOpen()?.handoffId !== handoff.handoffId) throw new Error('Your browser could not keep this recovery step. Allow site storage and try again.');
        window.location.assign('/dashboard');
      } catch (err) {
        setError(toUserFacingMessage(err, 'The extension could not open this project. Try again.'));
      }
      return;
    }
    if (projects.isLoading || projects.isFetching) return;
    ranAttempt.current = attempt;
    if (projects.isError) {
      log.warn('[OpenSitePage] projects did not load', { attempt });
      capture('open_site_failed', { stage: 'projects' });
      setError(FALLBACK);
      return;
    }
    const list = projects.data ?? [];
    const found = findSiteProject(list, site);
    log.info('[OpenSitePage] opening', { host, attempt, existing: !!found, retryProject: !!createdProjectId.current });
    openSite(site, {
      projects: list,
      teamId,
      extensionDetected,
      knownProjectId: createdProjectId.current,
      createProject: input => createProject.mutateAsync(input),
      createLiveReview: input => createLiveReview.mutateAsync(input),
    }).then((result) => {
      clearFreeToolSite();
      capture('open_site_opened', { project: createdProjectId.current ? 'created' : result.project });
      window.location.assign(result.url);
    }).catch((err: unknown) => {
      const stage = err instanceof OpenSiteError ? err.stage : 'review';
      if (err instanceof OpenSiteError && err.projectId && !found) createdProjectId.current = err.projectId;
      capture('open_site_failed', { stage });
      setError(toUserFacingMessage(err, FALLBACK));
    });
  // The mutations' identities change every render; the run is keyed by attempt instead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, needsOnboarding, teamId, projects.isLoading, projects.isFetching, projects.isError, attempt,
    extensionMode, requestedProjectId, requestedProject.isLoading, requestedProject.isFetching, requestedProject.isError, session]);

  if (!site) return <Navigate to="/dashboard" replace />;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;

  if (error) {
    return (
      <AppLoadingShell skeleton={false}>
        <section className="card-sm max-w-xl" role="alert">
          <h2 className="text-base font-semibold text-gray-900 break-words">Couldn&apos;t open {host} {extensionMode ? 'in the extension' : 'in team review'}</h2>
          <p className="mt-2 text-sm text-gray-600 break-words">{error}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="button" className="btn-primary" onClick={() => {
              setError(null);
              if (projects.isError) void projects.refetch();
              if (requestedProject.isError) void requestedProject.refetch();
              if (!session && sessionError) void refetchSession();
              setAttempt(value => value + 1);
            }}><span className="text-cap-trim">Try again</span></button>
            <Link to="/dashboard" className="btn-secondary"><span className="text-cap-trim">Back to dashboard</span></Link>
          </div>
        </section>
      </AppLoadingShell>
    );
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" aria-hidden="true" />
      <p className="text-sm text-gray-500 break-words" role="status">Opening {host} {extensionMode ? 'in the extension' : 'in team review'}...</p>
    </main>
  );
}
